import { useEffect, useId, useRef, useState } from 'react';
import {
  annonceRattachable,
  type AnnonceResume,
  type CandidatureDetail,
} from '@rh/shared';
import { libelleAnnonceRattachement } from '../annonces-choix';
import { appel } from '../api';
import { Erreur } from './Etat';

interface Props {
  candidatureId: string;
  /** Annonce actuelle : jamais proposée comme destination. */
  annonceId: string;
  annonceIntitule: string;
  nomCandidat: string;
  onChange: (fiche: CandidatureDetail) => void;
}

/**
 * « Déplacer vers une autre annonce » (droit `deplacerCandidature` donné par le
 * super admin, migration 024) : la candidature CHANGE d'annonce — elle n'est
 * pas recopiée, contrairement au rattachement depuis le vivier. Tout la suit :
 * appels, évaluation, documents, chartes.
 *
 * Le choix se fait dans une boîte de dialogue, sur la liste des annonces qui
 * acceptent une candidature (ni brouillon, ni archivée) ; l'annonce actuelle
 * en est retirée. Le bouton n'apparaît pas sans le droit : la page ne le
 * décide pas seule, l'API refuse aussi.
 */
export function DeplacerCandidature({ candidatureId, annonceId, annonceIntitule, nomCandidat, onChange }: Props) {
  const [ouvert, setOuvert] = useState(false);
  const [annonces, setAnnonces] = useState<AnnonceResume[] | null>(null);
  const [choix, setChoix] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const annuler = useRef<HTMLButtonElement>(null);
  const titre = useId();

  useEffect(() => {
    if (!ouvert) return;
    // Le focus part sur « Annuler » : rien ne se déplace sous la touche Entrée.
    annuler.current?.focus();
    const clavier = (evenement: KeyboardEvent) => {
      if (evenement.key !== 'Escape' || envoi) return;
      setOuvert(false);
      bouton.current?.focus();
    };
    document.addEventListener('keydown', clavier);
    return () => document.removeEventListener('keydown', clavier);
  }, [ouvert, envoi]);

  useEffect(() => {
    if (!ouvert || annonces) return;
    appel<AnnonceResume[]>('/annonces')
      .then((liste) => setAnnonces(liste.filter((a) => annonceRattachable(a.statut) && a.id !== annonceId)))
      .catch(() => setAnnonces([]));
  }, [ouvert, annonces, annonceId]);

  function fermer(): void {
    setOuvert(false);
    setErreur(null);
    setChoix('');
    bouton.current?.focus();
  }

  async function deplacer(): Promise<void> {
    const cible = annonces?.find((a) => a.id === choix);
    if (!cible) {
      setErreur('Choisir l’annonce de destination');
      return;
    }
    setEnvoi(true);
    setErreur(null);
    try {
      const fiche = await appel<CandidatureDetail>(`/candidatures/${candidatureId}/deplacer`, {
        method: 'POST',
        body: JSON.stringify({ annonceId: choix }),
      });
      onChange(fiche);
      setOuvert(false);
      setChoix('');
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Déplacement impossible');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <>
      <button ref={bouton} type="button" className="bouton-second" onClick={() => setOuvert(true)}>
        Déplacer vers une autre annonce
      </button>
      {ouvert && (
        <div className="dialogue-fond">
          <dialog open className="dialogue dialogue-deplacer" aria-modal="true" aria-labelledby={titre}>
            <h2 id={titre}>Déplacer la candidature de {nomCandidat}</h2>
            <p className="mention">
              Elle quitte « {annonceIntitule} » pour l’annonce choisie, avec ses appels, son évaluation et ses
              documents. Son adéquation est recalculée face aux critères de la nouvelle annonce ; un dossier déjà noté
              passe « à réévaluer », la note reste figée.
            </p>

            <label className="champ">
              <span>Nouvelle annonce</span>
              <select value={choix} disabled={!annonces || envoi} onChange={(e) => setChoix(e.target.value)}>
                <option value="">choisir une annonce…</option>
                {(annonces ?? []).map((annonce) => (
                  <option key={annonce.id} value={annonce.id}>
                    {libelleAnnonceRattachement(annonce)}
                  </option>
                ))}
              </select>
            </label>
            {annonces?.length === 0 && <p className="mention">Aucune autre annonce ne peut recevoir cette candidature.</p>}
            {erreur && <Erreur message={erreur} />}

            <div className="actions">
              <button type="button" className="bouton" disabled={envoi || !choix} onClick={() => void deplacer()}>
                Déplacer la candidature
              </button>
              <button ref={annuler} type="button" className="lien" disabled={envoi} onClick={fermer}>
                Annuler
              </button>
            </div>
          </dialog>
        </div>
      )}
    </>
  );
}
